import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Bookmark, Lock } from 'lucide-react';
import useFavorites from '../../hooks/favorites/useFavorites';
import FavoritesList from './FavoritesList/FavoritesList';
import AnimatedLoadingScreen from '../../components/UIFeedback/AnimatedLoadingScreen/AnimatedLoadingScreen';
import ErrorScreen from '../../components/UIFeedback/ErrorScreen/ErrorScreen';
import { Button } from '../../components/ui/button';
import Logo from '../../components/Logo/Logo';

const Favorites = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { favorites, loading, error } = useFavorites();

  // Safeguard against undefined favorites data
  const favoritesData = favorites.allIds?.map((itemId) => favorites.byId[itemId]) || [];

  // Handle loading state
  if (loading) {
    return <AnimatedLoadingScreen message={t("loading_favorites", "Loading saved items...")} />;
  }

  // Handle error state
  if (error) {
    return (
      <ErrorScreen>
        <p>{error.message || t("generic_error_message")}</p>
      </ErrorScreen>
    );
  }

  // Not logged in state - matching reference design
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 sm:p-6 pt-16 sm:pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-primary/10 flex items-center justify-center mb-4 sm:mb-6"
        >
          <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
        </motion.div>
        
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="text-xl sm:text-2xl font-bold text-foreground mb-2 sm:mb-3 text-center"
        >
          {t('sign_in_to_view_favorites', 'Sign in to view saved items')}
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="text-center text-xs sm:text-sm text-muted-foreground mb-6 sm:mb-8 max-w-sm px-2"
        >
          {t('favorites_login_description', 'Create an account to save posts, animals, and services for quick access')}
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Button
            onClick={() => navigate('/login')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-semibold"
          >
            {t('sign_in', 'Sign In')}
          </Button>
        </motion.div>

        {/* Feature list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="mt-8 sm:mt-12 space-y-2.5 sm:space-y-3 w-full max-w-sm px-2"
        >
          <p className="text-[10px] sm:text-xs font-semibold text-foreground uppercase tracking-wide">
            {t('save_to_favorites', 'Save to your collection:')}
          </p>
          <ul className="space-y-1.5 sm:space-y-2">
            <li className="flex items-start gap-2 sm:gap-3">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t('feature_lost_found', 'Lost & found animal posts')}
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t('feature_pet_sitters', 'Pet sitter contacts')}
              </span>
            </li>
            <li className="flex items-start gap-2 sm:gap-3">
              <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {t('feature_alerts', 'Important alerts and PSAs')}
              </span>
            </li>
          </ul>
        </motion.div>
      </div>
    );
  }

  // Logged in but no saved items
  if (favoritesData.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 sm:p-6 pt-16 sm:pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-secondary flex items-center justify-center mb-3 sm:mb-4"
        >
          <Bookmark className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
        </motion.div>
        
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-xl sm:text-2xl font-bold text-foreground mb-2 text-center"
        >
          {t('no_favorites_yet', 'No Saved Items Yet')}
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center text-xs sm:text-sm text-muted-foreground px-4"
        >
          {t('no_favorites_message', 'Bookmark posts and places to see them here')}
        </motion.p>
      </div>
    );
  }

  // Has saved items - show the list
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-lg sm:text-xl md:text-2xl font-bold text-foreground"
              >
                {t('my_favorites', 'My Favorites')}
              </motion.h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
                {favoritesData.length} {favoritesData.length === 1 ? t('item', 'item') : t('items', 'items')}
              </p>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              aria-label={t('go_to_home', 'Go to home')}
            >
              <Logo className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex-shrink-0" />
            </button>
          </div>
        </div>
      </div>

      {/* Saved Items List */}
      <div className="max-w-4xl mx-auto px-3 py-3 sm:px-4 sm:py-4">
        <FavoritesList data={favoritesData} />
      </div>
    </div>
  );
};

export default Favorites;
